import React, { useEffect, useRef, useState } from 'react';
import Cropper, { Area, Point } from 'react-easy-crop';
import Webcam from 'react-webcam';
import {
  CameraButton,
  CloseIconWhite,
  RotateLeft,
  RotateRight,
  ZoomIn,
  ZoomOut,
} from 'src/assets/images';
import { CaptureImage } from 'src/features/onboarding/components';
import getCroppedImg from 'src/features/onboarding/context/crop-image';
import { cn } from 'src/utils/cn';
import { useIsMobile } from '../../hooks/use-is-mobile';
import imageCompression from 'browser-image-compression';

type Props = {
  show: boolean;
  onShow: () => void;
  onCapture: () => void;
  onRetake: () => void;
  onClose: () => void;
  onConfirm: (image: string) => void;
};

enum CurrentState {
  TAKE_SELFIE = 'take-selfie',
  VIEW_SELFIE = 'view-selfie',
}

const ASPECT = 5 / 4;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.05;
const MAX_ROTATION = 360;
const MIN_ROTATION = -360;

const titleMap = new Map([
  [
    CurrentState.TAKE_SELFIE,
    {
      title: 'Take a selfie',
      subTitle: 'Position your face in the frame and take your photo.',
      primaryBtn: 'Take photo',
    },
  ],
  [
    CurrentState.VIEW_SELFIE,
    {
      title: 'Now check the photo',
      subTitle: 'Make sure the photo is not blurry or obscured by any harsh light.',
      primaryBtn: 'Confirm',
      secondaryBtn: 'Retake',
    },
  ],
]);

function SelfieCaptureModal(props: Props) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const webcamRef = useRef<Webcam>(null);
  const isMobile = useIsMobile();

  const [src, setSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [rotation, setRotation] = useState<number>(0);
  const [zoom, setZoom] = useState<number>(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area>();
  const [currentState, setCurrentState] = useState<CurrentState>(CurrentState.TAKE_SELFIE);

  const handleKeyDown = (event: KeyboardEvent) => {
    event.preventDefault();
    if (event.key == 'Escape') {
      props.onClose();
      dialogRef.current?.close();
    }
    return;
  };

  const onCropComplete = (_: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const getBase64 = (file: File) => {
    return new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (reader.result && typeof reader.result === 'string') {
          resolve(reader.result);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const compressImage = async (image: string) => {
    const imageFile = await imageCompression.getFilefromDataUrl(image, 'personal-selfie');
    const compressedFile = await imageCompression(imageFile, {
      maxSizeMB: 1,
      maxWidthOrHeight: 640,
    });
    return await getBase64(compressedFile);
  };

  const onClose = () => {
    props.onClose();
  };

  const capture = React.useCallback(() => {
    if (webcamRef.current) {
      props.onCapture && props.onCapture();
      const imageSrc = webcamRef.current.getScreenshot();
      setSrc(imageSrc);
      setCurrentState(CurrentState.VIEW_SELFIE);
    }
  }, [webcamRef]);

  const onClick = async () => {
    if (currentState === CurrentState.TAKE_SELFIE) {
      capture();
      return;
    }

    try {
      if (croppedAreaPixels === undefined || src === null) return;
      const croppedImage = await getCroppedImg(src, croppedAreaPixels, rotation);

      if (croppedImage === null) return;
      const compressedFile = await compressImage(croppedImage);
      props.onConfirm(compressedFile);
    } catch (error) {
      console.error(error);
    }
  };

  const onRetry = () => {
    props.onRetake && props.onRetake();
    setSrc(null);
    setRotation(0);
    setZoom(1);

    setCurrentState(CurrentState.TAKE_SELFIE);
  };

  useEffect(() => {
    if (props.show) {
      dialogRef.current?.showModal();
    }
  }, [props.show]);

  useEffect(() => {
    if (props.onShow) {
      props.onShow();
    }

    dialogRef.current?.addEventListener('keydown', handleKeyDown);

    return () => {
      dialogRef.current?.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const primaryBtnClass = cn({
    ['h-10 w-38 text-base font-medium text-center rounded-8']: true,
    ['bg-white-50 hover:bg-grey-100 active:bg-grey-200 disabled:cursor-not-allowed disabled:bg-grey-700 text-black ']:
      currentState === CurrentState.TAKE_SELFIE,
    ['bg-red-600 text-white']: currentState === CurrentState.VIEW_SELFIE,
  });

  const dialogClass = cn({
    ['flex flex-col gap-4 items-center w-full h-full md:h-[735px] bg-black gap-y-2 md:max-w-232 backdrop:bg-black backdrop:opacity-95 backdrop:m-0 backdrop:p-0 md:rounded-30']:
      true,
    ['m-0 h-dvh min-w-full pt-8 px-4']: isMobile,
  });

  return (
    <dialog ref={dialogRef} className={dialogClass}>
      <button className="absolute z-10 top-4 w-7 h-7 right-4" onClick={onClose}>
        <img src={CloseIconWhite} alt="close" />
      </button>

      <div className="w-full mt-16 max-w-100 h-100">
        {currentState === CurrentState.TAKE_SELFIE ? (
          <CaptureImage ref={webcamRef} cameraFacingMode="front" mirrored />
        ) : (
          <div className="relative w-full max-w-100 h-100">
            <Cropper
              image={src ?? ''}
              crop={crop}
              rotation={rotation}
              zoom={zoom}
              aspect={ASPECT}
              onCropChange={setCrop}
              onRotationChange={setRotation}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
              showGrid
            />
          </div>
        )}
      </div>
      {currentState === CurrentState.VIEW_SELFIE && (
        <div className="flex gap-2">
          <img
            className="cursor-pointer w-7 h-7"
            src={RotateLeft}
            alt="rotate left"
            onClick={() => {
              if (rotation < MAX_ROTATION) setRotation(rotation + 1);
            }}
          />
          <img
            className="cursor-pointer w-7 h-7"
            src={RotateRight}
            alt="rotate right"
            onClick={() => {
              if (rotation > MIN_ROTATION) setRotation(rotation - 1);
            }}
          />
          <img
            className="cursor-pointer w-7 h-7"
            src={ZoomIn}
            alt="zoom in"
            onClick={() => {
              if (zoom < MAX_ZOOM) setZoom(zoom + ZOOM_STEP);
            }}
          />
          <img
            className="cursor-pointer w-7 h-7"
            src={ZoomOut}
            alt="zoom out"
            onClick={() => {
              if (zoom > 1) setZoom(zoom - ZOOM_STEP);
            }}
          />
        </div>
      )}

      <section className="flex flex-col items-center gap-4 py-4 mt-auto mb-8 md:mt-0">
        <h1 className="text-3xl text-white">{titleMap.get(currentState)?.title}</h1>

        <p className="text-lg text-center text-white">{titleMap.get(currentState)?.subTitle}</p>

        <div className="flex justify-center w-full gap-2">
          {currentState === CurrentState.VIEW_SELFIE && (
            <button
              className="h-10 text-base font-medium text-center text-white bg-black border border-white border-solid rounded-8 w-38"
              type="button"
              onClick={onRetry}
            >
              {titleMap.get(currentState)?.secondaryBtn}
            </button>
          )}

          {isMobile ? (
            <>
              {currentState === CurrentState.TAKE_SELFIE ? (
                <button type="button" onClick={onClick}>
                  <img src={CameraButton} className="w-16 h-16 xs:h-22 xs:w-22" />
                </button>
              ) : (
                <button className={primaryBtnClass} type="button" onClick={onClick}>
                  {titleMap.get(currentState)?.primaryBtn}
                </button>
              )}
            </>
          ) : (
            <button className={primaryBtnClass} type="button" onClick={onClick}>
              {titleMap.get(currentState)?.primaryBtn}
            </button>
          )}
        </div>
      </section>
    </dialog>
  );
}

export { SelfieCaptureModal };
